import { useMemo, useState } from "react";
import type { OnChangeFn, SortingState } from "@salestrak/ui";

import type { SortDirection } from "#app/lib/data";
import { buildOrderBy, buildSearchWhere, combineWhereConditions } from "#app/lib/query-utils";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// The ONE client-side list query state hook
// ---------------------------------------------------------------------------
//
// Every full-page entity list (issues, teams, members, workflows) needs the
// same three things: a search box, a sort state, and the `where`/`orderBy` pair
// those two compose into. This hook is that, and nothing else — it holds NO
// data and runs NO query, so it says nothing about the shape of a server type.
// The list's own config (which columns search matches, what the API sorts by
// before the user picks) lives in `lib/filters/list-configs.ts`.

/** Static per-entity description of how its list searches and sorts. */
export type ListQueryConfig = Omittable<{
  /** Columns the search box matches. Empty when `searchMode` is `"server"`. */
  searchFields: readonly string[];
  /**
   * How the API takes free text.
   * - `"columns"` (default): OR-fold `{ contains, mode: insensitive }` over
   *   `searchFields`.
   * - `"server"`: send the one `search: String` flag the where-input carries
   *   and let the server pick the columns. REQUIRED for the where-inputs that
   *   have no boolean combinators — `CustomFieldWhereInput`, `StageWhereInput`,
   *   `ReasonWhereInput`, `IssueTypeWhereInput`, `IssueSourceWhereInput` — an
   *   `OR` fold is not expressible there (read the input type in schema.gql).
   */
  searchMode?: "columns" | "server";
  /** Column the `orderBy` falls back to when the user has not sorted. */
  defaultSortField: string;
  /** Direction for `defaultSortField`. Defaults to ascending. */
  defaultSortDirection?: SortDirection;
  /** Sorting the table starts on. Defaults to none. */
  initialSorting?: SortingState;
  /**
   * Emit NO `orderBy` until the user sorts, so the API applies its own default
   * ordering. Members need this — core sorts them by a computed display name
   * that no single column reproduces.
   */
  deferOrderBy?: boolean;
  /**
   * Filter keys the API expects at the TOP level of `where` rather than nested
   * inside the `AND` fold. Named, not guessed: these are semantic flags on the
   * query input, not column predicates.
   */
  hoistedFilterKeys?: readonly string[];
}>;

export type UseListQueryStateOptions = Omittable<{
  /** Conditions AND-folded ahead of search and filters (scope, archived, …). */
  baseWhere?: Array<Record<string, unknown>>;
  /** The filter panel's contribution, AND-folded after search. */
  filterWhere?: Record<string, unknown>;
}>;

export type ListQueryState = {
  searchValue: string;
  setSearchValue: (value: string) => void;
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  /** The search predicate alone, for callers that fold it themselves. */
  searchWhere: Record<string, unknown> | undefined;
  /** Undefined only when the config sets `deferOrderBy` and nothing is sorted. */
  orderBy: Record<string, string> | undefined;
  /** base + search + filter, folded. */
  where: Record<string, unknown>;
};

const NO_SORTING: SortingState = [];

export function useListQueryState(
  config: ListQueryConfig,
  { baseWhere, filterWhere }: UseListQueryStateOptions = {},
): ListQueryState {
  const {
    searchFields,
    searchMode,
    defaultSortField,
    defaultSortDirection,
    initialSorting,
    deferOrderBy,
    hoistedFilterKeys,
  } = config;

  // ExpandableSearch already debounces — never debounce a second time here.
  const [searchValue, setSearchValue] = useState("");
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? NO_SORTING);

  const searchWhere = useMemo(() => {
    if (searchMode === "server") return searchValue ? { search: searchValue } : undefined;
    return buildSearchWhere(searchValue, searchFields);
  }, [searchValue, searchFields, searchMode]);

  const where = useMemo(() => {
    const conditions: Array<Record<string, unknown>> = [...(baseWhere ?? [])];

    // Split the filter panel's output into column predicates (which fold into
    // the AND) and semantic flags the API reads off the top level.
    const hoisted: Record<string, unknown> = {};
    const nested: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(filterWhere ?? {})) {
      if (hoistedFilterKeys?.includes(key)) hoisted[key] = value;
      else nested[key] = value;
    }

    if (searchWhere) conditions.push(searchWhere);
    if (Object.keys(nested).length > 0) conditions.push(nested);

    const combined = combineWhereConditions(conditions) ?? {};
    return Object.keys(hoisted).length > 0 ? { ...combined, ...hoisted } : combined;
  }, [baseWhere, filterWhere, searchWhere, hoistedFilterKeys]);

  const orderBy = useMemo(() => {
    if (deferOrderBy && sorting.length === 0) return undefined;
    return buildOrderBy(sorting, defaultSortField, defaultSortDirection);
  }, [sorting, defaultSortField, defaultSortDirection, deferOrderBy]);

  return { searchValue, setSearchValue, sorting, setSorting, searchWhere, orderBy, where };
}
